/**
 * How a release's own status is labelled and coloured.
 *
 * THE VOCABULARY IS Lyrixa'S, AND THE LABELS SAY SO. §22 forbids inventing provider
 * status values, and none of these is one: `PublisherReleaseStatus` is where a release
 * sits in *this app's* workflow, which is a different fact from LabelGrid's
 * `review_status`/`delivery_status`. Where a provider value exists it is rendered
 * verbatim beside the badge, never folded into it — see the PROVIDER FIELDS rows in
 * routes/PublisherRelease.tsx.
 *
 * `live` is the one label that makes a claim about the outside world, so it is the one
 * that must never be reachable by a timer. Nothing in this app sets it; only a
 * provider-confirmed delivery could. See the status vocabulary note in types/domain.ts.
 *
 * WHY THIS IS NOT IN THE BADGE COMPONENT. `releaseStatusLabel` is prose, used away from
 * the pill, and a `.tsx` file that exports both a component and a plain function trips
 * react-refresh/only-export-components. The map lives with the function so the pill and
 * the sentence cannot end up disagreeing about what a status is called.
 */
import { c } from '../styles/theme';
import type { PublisherReleaseStatus } from '../types/domain';

/** Label and colour per status. */
export const PUBLISHER_STATUS_STYLES: Record<
  PublisherReleaseStatus,
  { label: string; color: string; bg: string }
> = {
  draft: { label: 'Draft', color: c.text2, bg: c.surface3 },
  in_review: { label: 'In Review', color: c.accent, bg: c.accentSoft },
  processing: { label: 'Processing', color: c.accent, bg: c.accentSoft },
  live: { label: 'Live', color: c.success, bg: c.successSoft },
  failed: { label: 'Needs Attention', color: c.danger, bg: c.dangerSoft },
  takedown: { label: 'Taken Down', color: c.text2, bg: c.surface3 },
};

/** The label for a status, for prose and for tests. */
export function releaseStatusLabel(status: PublisherReleaseStatus): string {
  return PUBLISHER_STATUS_STYLES[status].label;
}
